/**
 * A way BACK from wherever a report sent you.
 *
 * Reports can already send a biller to a member: `open-member` switches app.tsx to the
 * profile, and the analytics hash (#report=sfhp-invoice) survives the trip, so returning
 * to the Reports tab lands on the right panel by itself.
 *
 * What was missing is the return leg. The deep link was one-way: once on the profile the
 * only route back was the sidebar, which drops you at the Report Library root with the
 * panel's program and date window reset — so the biller who fixed a DOB had to rebuild
 * the query before they could see their own fix take effect.
 *
 * A ticket is set by whoever sends the user away, and the banner on the far side offers
 * the way home. sessionStorage, not localStorage: a return ticket is about THIS visit and
 * should not outlive the tab. Every access is wrapped — storage throws in a private
 * window and in some embedded views, and a broken back button must not take the profile
 * down with it.
 */

const KEY = 'weavein:return-ticket';

export interface ReturnTicket {
  /** Shown on the button: "Back to <label>". */
  label: string;
  /** app.tsx tab to return to — dispatched as a `changeTab` event. */
  tab: string;
  /** Optional analytics hash (e.g. "report=sfhp-invoice") restored on the way back. */
  hash?: string;
}

export function setReturnTicket(ticket: ReturnTicket): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(ticket));
  } catch {
    /* storage unavailable — the deep link still works, there is just no banner */
  }
}

export function readReturnTicket(): ReturnTicket | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const t = JSON.parse(raw);
    return t && typeof t.label === 'string' && typeof t.tab === 'string' ? t : null;
  } catch {
    return null;
  }
}

export function clearReturnTicket(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Go back, restoring the report's hash FIRST so AnalyticsDashboard reads it as it mounts
 * (_readHashReport runs in its useState initialiser — setting the hash afterwards would
 * land on the Report Library root instead of the panel the user left).
 */
export function goBackToReport(ticket: ReturnTicket): void {
  clearReturnTicket();
  if (ticket.hash) {
    const { pathname, search } = window.location;
    window.history.replaceState(null, '', `${pathname}${search}#${ticket.hash}`);
  }
  window.dispatchEvent(new CustomEvent('changeTab', { detail: ticket.tab }));
}
